// LAT-620: pure builder for the QG "Fix with Claudia" ask-intent. Kept free of React and of the
// Convex client so it is trivially unit-testable. The component resolves real section labels (via
// the compiled framework structure) and passes them in; this builder only shapes the deterministic
// intent payload and prompt.

import { CLAUDIA_SKILL_QUALITY_GUARD_REMEDIATION } from "@repo/convex/assistant/skills/names";
import { humanizeKey } from "@repo/shared";

/**
 * Turn-scoped skill that encodes the remediation procedure + draft/clarify routing.
 *
 * Re-exported from the backend constant rather than retyped: `assistant/skills/names` exists so a
 * frontend surface attaching a skill by name breaks at compile time when the skill is renamed
 * instead of silently attaching one that no longer exists. That module is a dependency-free leaf,
 * so importing it costs this builder nothing (LAT-773).
 */
export const QUALITY_GUARD_REMEDIATE_SKILL = CLAUDIA_SKILL_QUALITY_GUARD_REMEDIATION;

export type QualityGuardFixFindingInput = {
  /** LAT-634: the finding's own id — the machine-readable anchor Claudia needs to act on
   * THIS finding without searching, even when a section has several open issues. */
  findingId: string;
  /** The finding's section/family key (grouped findings share one key across members). */
  canonicalKey: string;
  ruleId?: string | null;
  tabKey?: string | null;
  fieldId?: string | null;
  title: string;
  message?: string | null;
  /** Every aspect key still failing for this finding's family — not just the one the
   * title/message describe. Grouped findings collapse to one representative member for
   * title/message (see `runtime.ts` `buildFamilyFindingMessaging`), so without this list
   * Claudia only ever sees the first failing aspect. */
  missingAspects?: string[];
  remediation: {
    requiredFix?: string;
    clarificationQuestion?: string;
    /** Remediation route from the finding; only "claudia_clarification" changes routing. */
    remediationRoute?: string;
  } | null;
};

export type ClaudiaFixEntityContext = {
  entityTypeKey: string;
  entityId: string;
  entityName: string | null | undefined;
};

export type ClaudiaFixSectionLabels = {
  tabLabel?: string | null;
  fieldLabel?: string | null;
};

export type ClaudiaFixIntentContext = {
  kind: "entity-section";
  entityTypeKey: string;
  entityId: string;
  entityName: string;
  tabKey: string;
  tabLabel: string;
  fieldId: string;
  fieldLabel: string;
};

export type ClaudiaFixIntent = {
  context: ClaudiaFixIntentContext;
  /**
   * LAT-634 seam: the exact finding this click was for, as structured data alongside the
   * prose prompt. Kept as the single-finding shape for existing readers; `findingRefs`
   * below is what actually crosses to the backend.
   */
  findingRef: ClaudiaFixFindingRef;
  /**
   * LAT-935: the structured channel. Forwarded verbatim into `submitMessage`, where the
   * server re-reads each finding, verifies it belongs to this project, and renders the full
   * payload — every missing aspect included — into the prompt as a hidden content part.
   *
   * Array-shaped on the wire (the backend's `submitMessage` accepts up to
   * `QUALITY_GUARD_FINDING_REF_LIMIT`), but this builder only ever produces exactly one entry —
   * the Guardian panel has no multi-finding or aspect-level selection UI (see LAT-934), so there
   * is no path that sends more than one finding ref per submit. The array is `[findingRef]`.
   */
  findingRefs: ClaudiaFixFindingRef[];
  trigger: "ask-claudia";
  /**
   * The message the user actually sees: ONE sentence. Safe to be this short only because
   * `findingRefs` carries the payload — see `resolveClaudiaFixPrompt`.
   */
  initialPrompt: string;
  /**
   * The pre-LAT-935 full reference-line prompt, kept as a graceful fallback for any path
   * where the structured refs do not reach the submit payload. Not dead weight: it is what
   * `resolveClaudiaFixPrompt` falls back to, and it is the only thing standing between a
   * dropped-refs regression and Claudia getting a bare title.
   */
  fallbackPrompt: string;
  /**
   * LAT-935: submit on click instead of parking a draft in the composer. By the time the
   * user has finished orienting, Claudia is already working.
   */
  autoSubmit: true;
  startNewThread: true;
  attachedSkillNames: string[];
};

export type ClaudiaFixFindingRef = {
  findingId: string;
  canonicalKey: string;
  ruleId: string | null;
  missingAspects: string[];
};

/**
 * Pick the prompt text for an ask-intent: the short sentence when the structured finding
 * channel is carrying the payload, the full reference-line prompt when it is not.
 *
 * The founder's rule for LAT-935 is that shrinking the visible prompt must never shrink what
 * Claudia receives. This function is where that rule is enforced — the short text is only ever
 * used when there is something to be short *because of*.
 */
export function resolveClaudiaFixPrompt(intent: {
  initialPrompt: string;
  fallbackPrompt?: string;
  findingRefs?: { findingId: string }[];
}): string {
  if (intent.findingRefs && intent.findingRefs.length > 0) {
    return intent.initialPrompt;
  }
  return intent.fallbackPrompt ?? intent.initialPrompt;
}

function trimToUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Build the ask-intent for "Fix with Claudia" on a QG finding, or `null` when the finding
 * has no draftable section anchor (both a `tabKey` and `fieldId` are required to target a
 * section's active draft). The caller uses a non-null result as the button's visibility +
 * click payload.
 */
export function buildClaudiaFixIntent(args: {
  finding: QualityGuardFixFindingInput;
  entityCtx: ClaudiaFixEntityContext;
  labels: ClaudiaFixSectionLabels;
}): ClaudiaFixIntent | null {
  const { finding, entityCtx, labels } = args;

  const tabKey = trimToUndefined(finding.tabKey);
  const fieldId = trimToUndefined(finding.fieldId);
  // No section to draft into → no "Fix with Claudia" affordance.
  if (!tabKey || !fieldId) {
    return null;
  }

  const entityName = trimToUndefined(entityCtx.entityName) ?? "this entity";
  const tabLabel = trimToUndefined(labels.tabLabel) ?? humanizeKey(tabKey);
  const fieldLabel = trimToUndefined(labels.fieldLabel) ?? humanizeKey(fieldId);
  const location = `${entityName} › ${tabLabel} / ${fieldLabel}`;

  const requiredFix = trimToUndefined(finding.remediation?.requiredFix);
  const clarificationQuestion = trimToUndefined(finding.remediation?.clarificationQuestion);
  const message = trimToUndefined(finding.message);
  const isClarification = finding.remediation?.remediationRoute === "claudia_clarification";
  // LAT-934 founder decision (2026-08-24): no aspect-level selection — Claudia always attempts
  // to fix everything from the finding's full aspect list; what she can't resolve she marks as
  // open in her own reply. So every missing aspect is always sent, never a client-chosen subset.
  const missingAspects = (finding.missingAspects ?? []).filter(
    (aspect): aspect is string => typeof aspect === "string" && aspect.trim().length > 0
  );

  // LAT-634: a deterministic, machine-readable pointer to THIS finding — present on every
  // route (the clarify branch used to omit requiredFix/message entirely, leaving Claudia
  // with only the title). Aspect keys let Claudia address every gap a grouped finding
  // covers, not just the one its title/message names.
  const findingReferenceLine = `Finding reference: \`${finding.findingId}\` (section \`${finding.canonicalKey}\`)${
    missingAspects.length > 0 ? ` — missing: ${missingAspects.join(", ")}` : ""
  }`;
  const whatsNeededLine = requiredFix
    ? `What's needed: ${requiredFix}`
    : message
      ? `Issue: ${message}`
      : undefined;

  const lines = isClarification
    ? [
        "Help me resolve this Quality Guard issue by updating the section for my review:",
        `**${finding.title}** — ${location}`,
        findingReferenceLine,
        whatsNeededLine,
        `This needs clarification first — ask me what's required, then draft the fix.${
          clarificationQuestion ? ` Open question: ${clarificationQuestion}` : ""
        }`,
      ]
    : [
        "Fix this Quality Guard issue and update the section for my review:",
        `**${finding.title}** — ${location}`,
        findingReferenceLine,
        whatsNeededLine,
      ];

  // LAT-935: what the user reads. One sentence — the title and the place, nothing machine-facing.
  // The finding id, the aspect list and the required fix are not omitted, they moved: the server
  // renders them from the row behind `findingRefs`, which is strictly more than this text ever
  // carried (it re-reads every field rather than trusting what the client had on screen).
  //
  // LAT-934/#741 wording alignment: "update the section(s)" not "draft into the section(s)" —
  // the write actually commits immediately and Quality Guard auto-rescans right after, so "draft"
  // taught a false unsaved-draft mental model.
  const initialPrompt = isClarification
    ? `Help me resolve this Quality Guard issue and update the section for my review: **${finding.title}** — ${location}.`
    : `Fix this Quality Guard issue and update the section for my review: **${finding.title}** — ${location}.`;

  const findingRef = {
    findingId: finding.findingId,
    canonicalKey: finding.canonicalKey,
    ruleId: finding.ruleId ?? null,
    missingAspects,
  };

  return {
    context: {
      kind: "entity-section",
      entityTypeKey: entityCtx.entityTypeKey,
      entityId: entityCtx.entityId,
      entityName,
      tabKey,
      tabLabel,
      fieldId,
      fieldLabel,
    },
    findingRef,
    findingRefs: [findingRef],
    trigger: "ask-claudia",
    initialPrompt,
    fallbackPrompt: lines.filter((line): line is string => Boolean(line)).join("\n"),
    autoSubmit: true,
    startNewThread: true,
    attachedSkillNames: [QUALITY_GUARD_REMEDIATE_SKILL],
  };
}

/** A Quality Guard 2 scope Claudia can be asked to fix: a section, a whole tab, or the whole element. */
export type ClaudiaFixScope =
  | { kind: "section"; entityId: string; tabKey: string; fieldId: string }
  | { kind: "tab"; entityId: string; tabKey: string }
  | { kind: "element"; entityId: string };

/**
 * The ask-intent for "Fix with Claudia" where Quality Guard 2 is of record. It names a place,
 * never a finding: the server expands `findingsReference` to every open finding of that scope,
 * so the visible message is one sentence and Claudia still receives everything.
 */
export function buildClaudiaFixScopeIntent(args: {
  scope: ClaudiaFixScope;
  entityCtx: ClaudiaFixEntityContext;
  labels: ClaudiaFixSectionLabels;
  scopeLabel: string;
}) {
  const { scope, entityCtx, labels } = args;
  const entityName = trimToUndefined(entityCtx.entityName) ?? "this entity";
  const element = {
    entityTypeKey: entityCtx.entityTypeKey,
    entityId: entityCtx.entityId,
    entityName,
  };
  const context =
    scope.kind === "section"
      ? {
          kind: "entity-section" as const,
          ...element,
          tabKey: scope.tabKey,
          tabLabel: trimToUndefined(labels.tabLabel) ?? humanizeKey(scope.tabKey),
          fieldId: scope.fieldId,
          fieldLabel: trimToUndefined(labels.fieldLabel) ?? humanizeKey(scope.fieldId),
        }
      : scope.kind === "tab"
        ? {
            kind: "entity-tab" as const,
            ...element,
            tabKey: scope.tabKey,
            tabLabel: trimToUndefined(labels.tabLabel) ?? humanizeKey(scope.tabKey),
          }
        : { kind: "entity-tab" as const, ...element };
  return {
    context,
    trigger: "ask-claudia" as const,
    initialPrompt: `Fix the Quality Guard issues in ${args.scopeLabel} of ${entityName} and update it for my review.`,
    findingsReference: { entityId: entityCtx.entityId, scope },
    autoSubmit: true as const,
    startNewThread: true as const,
    attachedSkillNames: [QUALITY_GUARD_REMEDIATE_SKILL],
  };
}
